import React, { createContext, useContext, useMemo } from "react";
import { ConfigProvider } from "antd";

export type Density = "default" | "compact";

/**
 * Control size is a property of *where* a control sits, not of the control.
 *
 * DESIGN_SYSTEM.md §5 makes 48px the default and 40px the compact size, and the
 * agreed reading (plan Q2, option b) is that scrollable property panels and
 * filter rails use compact throughout. Threading a `size` prop through every call
 * site would mean every author remembering; a context means the container decides
 * once and everything inside it follows.
 *
 * Compact also tightens the inner padding, 16px to 12px. A 40px field with 16px
 * of inset reads loose next to a 48px one, and the two sizes have to line up
 * their text when they sit in the same column.
 */
const DensityContext = createContext<Density>("default");

export const DensityProvider = DensityContext.Provider;

export const useDensity = (override?: Density): Density => {
	const inherited = useContext(DensityContext);
	return override ?? inherited;
};

/** Resolved control height, as a CSS custom-property reference. */
export const controlHeight = (density: Density) =>
	density === "compact" ? "var(--size-control-compact)" : "var(--size-control)";

/** Resolved inner padding, as a CSS custom-property reference. */
export const controlPadding = (density: Density) =>
	density === "compact" ? "var(--space-3)" : "var(--space-4)";

export interface DenseProps {
	children: React.ReactNode;
}

/**
 * Makes everything inside compact — hds primitives *and* antd controls.
 *
 * The two need telling separately: hds reads `DensityContext`, antd reads a
 * nested `ConfigProvider`. Providing only the first was a gap — an antd Select
 * inside an hds Panel stayed 48px while its hds neighbours dropped to 40, which
 * is exactly the mismatch the library filter rail showed.
 *
 * Compact is 40px in both, and their text insets land within a pixel of each
 * other — 12px for an hds field, ~12px for an antd one.
 */
export const Dense = ({ children }: DenseProps) => {
	// Height only. antd already insets its field text by ~12px (paddingSM minus
	// the border), which is what `controlPadding` matches in compact — so there is
	// nothing to override, and no dependence on an antd internal that has no
	// public token anyway.
	const theme = useMemo(() => ({ token: { controlHeight: 40, controlHeightLG: 40 } }), []);

	return (
		<DensityProvider value="compact">
			<ConfigProvider theme={theme}>{children}</ConfigProvider>
		</DensityProvider>
	);
};

export default DensityContext;
